import { useState } from 'react';
import { cn } from '@/utils/cn';
import {
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ArrowsRightLeftIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { Badge } from '@/components/ui';
import type { SpatialGoalResult, AxisGoalResult, TimingGoalResult } from '@/types/api';
import {
  degradedCheckpointNote,
  withheldCheckpointReason,
} from '@/utils/checkpointCoverageCopy';
import { plainGoalExplanation } from '@/utils/goalExplanationCopy';
import {
  travelReportRow,
  travelTargetSourcePhrase,
  travelFloorAppliedNote,
  TRAVEL_BODY_HEIGHT_DEFINITION_LINE,
} from '@/utils/travelDistanceCopy';
import { VelocityTimeline } from './VelocityTimeline';

// ============================================================================
// Goal type detection helpers
// ============================================================================

type GoalResult = SpatialGoalResult | AxisGoalResult | TimingGoalResult;

type GoalCategory = 'spatial' | 'movement' | 'axis' | 'timing' | 'joint_angle';

function getGoalCategory(annotationId: string): GoalCategory {
  if (annotationId.endsWith('_movement')) return 'movement';
  if (annotationId.endsWith('_axis')) return 'axis';
  if (annotationId.endsWith('_timing')) return 'timing';
  if (annotationId.endsWith('_joint_angle')) return 'joint_angle';
  return 'spatial';
}

function formatRegionName(annotationId: string): string {
  const cleanId = annotationId
    .replace(/_movement$/i, '')
    .replace(/_axis$/i, '')
    .replace(/_timing$/i, '')
    .replace(/_joint_angle$/i, '');

  return cleanId
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ============================================================================
// Category visual config — the "instrument panel" identity system
// ============================================================================

interface CategoryStyle {
  label: string;
  /** Tailwind color token for the accent (used in bg-{color}/x, text-{color}, border-{color}/x) */
  accentClass: string;
  /** CSS color value for inline styles (left strip, glow) */
  accentColor: string;
}

const CATEGORY_STYLES: Record<GoalCategory, CategoryStyle> = {
  spatial: {
    label: 'Spatial',
    accentClass: 'accent-blue',
    accentColor: '#3DA9FC',
  },
  movement: {
    label: 'Movement',
    accentClass: 'accent-orange',
    accentColor: '#FF8A3D',
  },
  axis: {
    label: 'Axis',
    accentClass: 'accent-gold',
    accentColor: '#F4C95D',
  },
  timing: {
    label: 'Timing',
    accentClass: 'info',
    accentColor: '#3DA9FC',
  },
  joint_angle: {
    label: 'Joint Angle',
    accentClass: 'accent',
    accentColor: '#1ED760',
  },
};

const CATEGORY_ORDER: GoalCategory[] = ['spatial', 'movement', 'axis', 'timing', 'joint_angle'];

const CATEGORY_SECTION_LABELS: Record<GoalCategory, string> = {
  spatial: 'Spatial Goals',
  movement: 'Movement Goals',
  axis: 'Axis Alignment',
  timing: 'Timing Coordination',
  joint_angle: 'Joint Angles',
};

// ============================================================================
// Type guards
// ============================================================================

function isAxisGoalResult(result: GoalResult): result is AxisGoalResult {
  return 'alignment_score' in result && 'stability_score' in result;
}

function isTimingGoalResult(result: GoalResult): result is TimingGoalResult {
  return 'timing_score' in result && !('alignment_score' in result);
}

function isSpatialGoalResult(result: GoalResult): result is SpatialGoalResult {
  return !isAxisGoalResult(result) && !isTimingGoalResult(result);
}

// ============================================================================
// Formatting helpers
// ============================================================================

function fmtPct(value: number | null | undefined): string {
  // `null` is a WITHHELD measurement, not a zero (LFT-063 sentinel rule) —
  // both absent and withheld render as an em-dash, never as "0%".
  if (value === undefined || value === null) return '—';
  return `${Math.round(value)}%`;
}

function fmtAngle(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${value.toFixed(1)}°`;
}

function fmtFrames(frames: number | undefined): string {
  if (frames === undefined) return '—';
  return frames >= 0 ? `+${frames}f` : `${frames}f`;
}

function fmtRatio(value: number | undefined): string {
  if (value === undefined) return '—';
  return value.toFixed(2);
}

function fmtLandmark(name: string): string {
  return name
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ============================================================================
// Main component
// ============================================================================

interface GoalResultsSectionProps {
  spatialGoalResults?: Record<string, GoalResult>;
  className?: string;
  /**
   * 2026-09-21 report-redesign Phase 1 (owner decision 2, "words-first WITH
   * expandable details"): goal cards are expandable for ALL roles by default.
   * This RETIRES the 2026-07 Q1 ruling that hid the expand chevron and
   * disabled the card button for athletes (allowExpand was previously wired
   * to `isCoach` at the EvalLedger call site). The detail panel itself stays
   * words-first — checkpoint scores are never shown as raw numbers in the
   * card header (label-only, decision 2); the expanded panel shows the
   * existing measured metrics plus the coach's note and an honest
   * "not yet calibrated" callout (decision 1) until the backend's
   * `qualitative.band` envelope ships (P2 — see
   * backlog/eval-report-redesign-artifact-inspired.md).
   * Set to false only for surfaces that intentionally want a flat,
   * non-interactive list (kept for backward compatibility / future use).
   */
  allowExpand?: boolean;
}

function GoalResultsSection({ spatialGoalResults, className, allowExpand = true }: GoalResultsSectionProps) {
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());

  if (!spatialGoalResults || Object.keys(spatialGoalResults).length === 0) {
    return null;
  }

  const grouped: Partial<Record<GoalCategory, Array<[string, GoalResult]>>> = {};
  for (const [annotationId, result] of Object.entries(spatialGoalResults)) {
    const category = getGoalCategory(annotationId);
    if (!grouped[category]) grouped[category] = [];
    grouped[category]!.push([annotationId, result]);
  }

  const toggleGoal = (annotationId: string) => {
    setExpandedGoals((prev) => {
      const next = new Set(prev);
      if (next.has(annotationId)) {
        next.delete(annotationId);
      } else {
        next.add(annotationId);
      }
      return next;
    });
  };

  return (
    <div className={cn('space-y-5', className)}>
      {CATEGORY_ORDER.map((category) => {
        const entries = grouped[category];
        if (!entries || entries.length === 0) return null;
        const style = CATEGORY_STYLES[category];

        return (
          <div key={category}>
            {/* Section heading with colored accent bar */}
            <div className="flex items-center gap-2.5 mb-3">
              <div
                className="w-1 h-4 rounded-full"
                style={{ backgroundColor: style.accentColor }}
              />
              <h3 className="text-xs font-semibold uppercase tracking-widest text-text-muted font-heading">
                {CATEGORY_SECTION_LABELS[category]}
              </h3>
            </div>

            <div className="space-y-2">
              {entries.map(([annotationId, result]) => (
                <GoalResultCard
                  key={annotationId}
                  annotationId={annotationId}
                  result={result}
                  category={category}
                  style={style}
                  isExpanded={allowExpand && expandedGoals.has(annotationId)}
                  onToggle={allowExpand ? () => toggleGoal(annotationId) : undefined}
                  allowExpand={allowExpand}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Goal result card
// ============================================================================

interface GoalResultCardProps {
  annotationId: string;
  result: GoalResult;
  category: GoalCategory;
  style: CategoryStyle;
  isExpanded: boolean;
  onToggle?: () => void;
  /**
   * When false, hides the expand chevron and suppresses the detail panel.
   * Defaults to true for all roles as of the 2026-09-21 report redesign
   * (decision 2 retires the athlete-only disabled-button pattern).
   */
  allowExpand?: boolean;
}

function GoalResultCard({
  annotationId,
  result,
  category,
  style,
  isExpanded,
  onToggle,
  allowExpand = true,
}: GoalResultCardProps) {
  // Prefer human-readable label from the result; fall back to formatted ID
  const regionName = result.annotation_label
    || result.annotation_description
    || formatRegionName(annotationId);
  const passed = result.passed;
  const skipped = isSpatialGoalResult(result) ? (result.skipped ?? false) : false;

  // Viewpoint observability Phase 1. `status` is absent on every evaluation
  // produced before the contract — absence must read as "computed".
  const status = result.status ?? 'computed';
  const abstained = status === 'abstained';
  const degraded = status === 'degraded';

  return (
    <div
      className={cn(
        'relative rounded-lg border overflow-hidden',
        'transition-all duration-200',
        skipped || abstained
          ? 'bg-dark-elevated-1 border-border-subtle'
          : 'bg-dark-elevated-1/80 border-border-subtle hover:border-border-strong',
      )}
    >
      {/* Left accent strip — neutral for a withheld check: a pass/fail colour
          on a measurement that was never taken is a fabricated verdict. */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{
          backgroundColor: skipped || abstained
            ? 'var(--color-text-muted)'
            : passed
            ? 'var(--color-success)'
            : 'var(--color-danger)',
        }}
      />

      {/* Header — renders as a button only when expansion is allowed */}
      <button
        onClick={allowExpand ? onToggle : undefined}
        disabled={!allowExpand}
        className={cn(
          'w-full flex items-center gap-3 pl-4 pr-3 py-3',
          allowExpand && 'hover:bg-dark-elevated-2/40 transition-colors duration-150',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset',
        )}
      >
        {/* Status icon */}
        {abstained ? (
          <EyeSlashIcon className="h-5 w-5 flex-shrink-0 text-text-muted" />
        ) : skipped ? (
          <XCircleIcon className="h-5 w-5 flex-shrink-0 text-text-muted" />
        ) : passed ? (
          <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-success" />
        ) : (
          <XCircleIcon className="h-5 w-5 flex-shrink-0 text-danger" />
        )}

        {/* Name + category pill */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="font-semibold text-sm text-text-primary truncate">
            {regionName}
          </span>
          <span
            className="text-[10px] font-medium px-1.5 py-px rounded-full flex-shrink-0 uppercase tracking-wide"
            style={{
              backgroundColor: `${style.accentColor}18`,
              color: style.accentColor,
            }}
          >
            {style.label}
          </span>
        </div>

        {/* Badge + chevron — label-only header (decision 2, 2026-09-21 report
            redesign): no raw checkpoint score is shown here. Per-goal
            calibrated bands aren't in the backend payload yet (P2), so
            showing a bucketed number here would misrepresent it as
            calibrated. See the "not yet calibrated" note in the detail panel. */}
        <div className="flex items-center gap-2.5 flex-shrink-0">
          {abstained ? (
            <Badge variant="secondary" size="sm">NOT SCORED</Badge>
          ) : skipped ? (
            <Badge variant="secondary" size="sm">SKIP</Badge>
          ) : (
            <Badge variant={passed ? 'success' : 'error'} size="sm">
              {passed ? 'PASS' : 'FAIL'}
            </Badge>
          )}

          {/* Chevron only shown when expansion is permitted (coach view) */}
          {allowExpand && (
            <ChevronDownIcon
              className={cn(
                'h-4 w-4 text-text-muted transition-transform duration-200',
                isExpanded && 'rotate-180',
              )}
            />
          )}
        </div>
      </button>

      {/* Withheld check — always visible (not behind the expander): the coach
          or athlete must see WHY no score exists without having to dig.
          Copy from @/utils/checkpointCoverageCopy (one source for the report
          rows and this card). */}
      {abstained && (
        <p
          data-testid="goal-card-withheld-note"
          className="px-4 pb-3 -mt-1 text-xs leading-relaxed text-text-secondary"
        >
          {withheldCheckpointReason(
            result.abstention_reason ?? 'evaluation_error',
            result.body_part,
          )}
        </p>
      )}

      {/* Degraded check — the score below IS shown; this says how firmly to
          read it. Label-only: the number itself is unchanged. */}
      {degraded && (
        <p
          data-testid="goal-card-degraded-note"
          className="px-4 pb-3 -mt-1 text-xs leading-relaxed text-text-secondary"
        >
          {degradedCheckpointNote(result.degrade_reason)}
        </p>
      )}

      {/* Expanded detail panel — only when allowExpand is true */}
      {allowExpand && isExpanded && (
        <div className="border-t border-border-subtle/40">
          {isAxisGoalResult(result) && (
            <AxisGoalDetails result={result} accentColor={style.accentColor} />
          )}
          {isTimingGoalResult(result) && (
            <TimingGoalDetails result={result} accentColor={style.accentColor} />
          )}
          {isSpatialGoalResult(result) && (
            <SpatialGoalDetails
              result={result}
              isMovement={category === 'movement'}
              accentColor={style.accentColor}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Metric display atoms
// ============================================================================

function MetricCell({ label, value, highlight }: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="px-3 py-2.5 rounded-md bg-dark-base/50">
      <p className="text-[10px] font-medium text-text-muted uppercase tracking-wider mb-0.5">
        {label}
      </p>
      <p
        className={cn(
          'text-sm font-semibold tabular-nums',
          highlight ? 'text-text-primary' : 'text-text-secondary',
        )}
      >
        {value}
      </p>
    </div>
  );
}

function HandednessIndicator({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-accent-blue/5 border border-accent-blue/15">
      <ArrowsRightLeftIcon className="h-3.5 w-3.5 text-accent-blue mt-0.5 flex-shrink-0" />
      <div className="text-xs text-text-secondary leading-relaxed">
        {children}
      </div>
    </div>
  );
}

/**
 * Coach's note — renders `annotation_notes` in the athlete-visible detail
 * panel (2026-09-21 report redesign, decisions 2 + 4). This is the same
 * field the backend's future source_quote -> notes hop (P2, epic E6) will
 * enrich; today it shows whatever plain notes the coach already wrote for
 * this checkpoint. Quote-styled content like this is authenticated-report
 * only — PublicSharePage's curated payload never includes annotation_notes
 * (see PublicSharePage.noQuotes.test.tsx).
 */
function CoachNoteBlock({ note }: { note?: string | null }) {
  if (!note) return null;
  return (
    <div className="px-3 py-2.5 rounded-md bg-dark-base/50 border-l-2 border-l-accent/40">
      <p className="text-[10px] font-medium text-text-muted uppercase tracking-wider mb-1">
        Coach&rsquo;s note
      </p>
      <p className="text-xs text-text-secondary leading-relaxed italic">
        &ldquo;{note}&rdquo;
      </p>
    </div>
  );
}

/**
 * Honest-limits callout (decision 1 + epic E7): every checkpoint card is
 * calibration-gated. There is currently no per-goal `qualitative.band`
 * field in the evaluation payload (P2 — flag-independent sigma injection +
 * TSV projection, see backlog/eval-report-redesign-artifact-inspired.md
 * "P2 — backend additive display fields"), so we say so plainly instead of
 * inventing a word bucket from the raw score. Do NOT replace this with a
 * generic 80/60 threshold bucket — decision 1 explicitly forbids
 * backfilling checkpoint words from the global thresholds.
 */
function CalibrationHonestNote() {
  return (
    <p className="text-[11px] text-text-muted leading-relaxed">
      Not yet calibrated against your coach&rsquo;s example takes — this
      checkpoint shows the pass/fail check and raw measurements below.
    </p>
  );
}

// ============================================================================
// Axis goal detail
// ============================================================================

function AxisGoalDetails({ result, accentColor: _accentColor }: {
  result: AxisGoalResult;
  accentColor: string;
}) {
  const hasEffective =
    result.effective_landmark_start !== undefined || result.effective_landmark_end !== undefined;

  return (
    <div className="px-4 py-3 space-y-3">
      <CalibrationHonestNote />

      {/* Primary metrics row */}
      <div className="grid grid-cols-3 gap-1.5">
        <MetricCell label="Alignment" value={fmtPct(result.alignment_score)} highlight />
        <MetricCell label="Stability" value={fmtPct(result.stability_score)} highlight />
        <MetricCell label="Confidence" value={fmtPct(result.confidence * 100)} />
      </div>

      {/* Angle metrics row */}
      <div className="grid grid-cols-3 gap-1.5">
        <MetricCell label="Mean Angle" value={fmtAngle(result.mean_angle)} />
        <MetricCell label="Std Dev" value={fmtAngle(result.std_dev_angle)} />
        <MetricCell label="Max Dev" value={fmtAngle(result.max_deviation)} />
      </div>

      {/* Supplementary row */}
      <div className="grid grid-cols-3 gap-1.5">
        {result.template_mean_angle !== undefined && (
          <MetricCell label="Template" value={fmtAngle(result.template_mean_angle)} />
        )}
        <MetricCell
          label="Valid Frames"
          value={`${result.valid_frames}/${result.total_frames}`}
        />
      </div>

      {/* Handedness mirroring indicator */}
      {hasEffective && (
        <HandednessIndicator>
          <span className="text-text-muted">Mirrored for handedness — evaluated </span>
          <span className="text-text-primary font-medium">
            {result.effective_landmark_start
              ? fmtLandmark(result.effective_landmark_start)
              : '—'}
          </span>
          <span className="text-text-muted mx-1">→</span>
          <span className="text-text-primary font-medium">
            {result.effective_landmark_end
              ? fmtLandmark(result.effective_landmark_end)
              : '—'}
          </span>
        </HandednessIndicator>
      )}

      {/* Coach's note */}
      <CoachNoteBlock note={result.annotation_notes} />

      {/* Explanation */}
      {result.explanation && (
        <p className="text-xs text-text-secondary leading-relaxed pt-1 border-t border-border-subtle/30">
          {plainGoalExplanation(result.explanation)}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Timing goal detail
// ============================================================================

function TimingGoalDetails({ result, accentColor: _accentColor }: {
  result: TimingGoalResult;
  accentColor: string;
}) {
  const hasEffective =
    result.effective_landmark_a !== undefined || result.effective_landmark_b !== undefined;

  return (
    <div className="px-4 py-3 space-y-3">
      <CalibrationHonestNote />

      {/* Primary metrics */}
      <div className="grid grid-cols-3 gap-1.5">
        <MetricCell label="Score" value={fmtPct(result.timing_score)} highlight />
        <MetricCell label="Confidence" value={fmtPct(result.confidence * 100)} />
        {result.lag_within_bounds !== undefined && (
          <MetricCell
            label="Bounds"
            value={result.lag_within_bounds ? 'Within' : 'Outside'}
            highlight={!result.lag_within_bounds}
          />
        )}
      </div>

      {/* Lag metrics */}
      <div className="grid grid-cols-3 gap-1.5">
        {result.measured_lag_frames !== undefined && (
          <MetricCell label="Measured Lag" value={fmtFrames(result.measured_lag_frames)} />
        )}
        {result.template_lag_frames !== undefined && (
          <MetricCell label="Template Lag" value={fmtFrames(result.template_lag_frames)} />
        )}
        {result.lag_deviation_frames !== undefined && (
          <MetricCell label="Deviation" value={fmtFrames(result.lag_deviation_frames)} />
        )}
      </div>

      {/* Event frame indicators */}
      {(result.landmark_a_event_frame !== undefined ||
        result.landmark_b_event_frame !== undefined) && (
        <div className="grid grid-cols-2 gap-1.5">
          {result.landmark_a_event_frame !== undefined && (
            <MetricCell label="Event A" value={`frame ${result.landmark_a_event_frame}`} />
          )}
          {result.landmark_b_event_frame !== undefined && (
            <MetricCell label="Event B" value={`frame ${result.landmark_b_event_frame}`} />
          )}
        </div>
      )}

      {/* Velocity timeline — rendered when velocity signals are available */}
      {(result.velocity_signal_a || result.velocity_signal_b) && (
        <div className="pt-1 border-t border-border-subtle/30">
          <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">
            Velocity Timeline
          </p>
          <VelocityTimeline result={result} />
        </div>
      )}

      {/* Handedness mirroring indicator */}
      {hasEffective && (
        <HandednessIndicator>
          <span className="text-text-muted">Mirrored for handedness — </span>
          <span className="text-text-primary font-medium">
            A: {result.effective_landmark_a ? fmtLandmark(result.effective_landmark_a) : '—'}
          </span>
          <span className="text-text-muted mx-1">·</span>
          <span className="text-text-primary font-medium">
            B: {result.effective_landmark_b ? fmtLandmark(result.effective_landmark_b) : '—'}
          </span>
        </HandednessIndicator>
      )}

      {/* Coach's note */}
      <CoachNoteBlock note={result.annotation_notes} />

      {/* Explanation */}
      {result.explanation && (
        <p className="text-xs text-text-secondary leading-relaxed pt-1 border-t border-border-subtle/30">
          {plainGoalExplanation(result.explanation)}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Spatial / movement goal detail
// ============================================================================

function SpatialGoalDetails({
  result,
  isMovement,
  accentColor: _accentColor,
}: {
  result: SpatialGoalResult;
  isMovement: boolean;
  accentColor: string;
}) {
  // Travel-distance target (TRAVEL_PR_A_CONTRACT.md §3) — its own detail
  // block, read FLAT off `result` (the contract's source of truth; not the
  // `trajectory_result` duplicate). Abstained goals already show their
  // reason in the card header (checkpointCoverageCopy's generic withheld
  // note) — nothing further to say here when there is no measurement.
  const isTravel = isMovement && !!result.travel_target_mode;
  const travelComputed = isTravel && result.status !== 'abstained';

  return (
    <div className="px-4 py-3 space-y-3">
      <CalibrationHonestNote />

      {travelComputed ? (
        <div className="space-y-2" data-testid="travel-result-details">
          <p className="text-sm font-medium text-text-primary">
            {travelReportRow(
              result.annotation_label || result.annotation_description || 'This checkpoint',
              result.measured_travel_body_heights,
              result.target_body_heights,
            )}
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            <MetricCell
              label="Target source"
              value={travelTargetSourcePhrase(result.target_source_resolved ?? null)}
            />
            {result.coverage !== undefined && (
              <MetricCell label="Confidence" value={fmtPct(result.coverage * 100)} highlight />
            )}
          </div>
          <p className="text-xs text-text-secondary">{TRAVEL_BODY_HEIGHT_DEFINITION_LINE}</p>
          {result.tolerance_floor_applied && (
            <p className="text-xs text-text-muted" data-testid="travel-floor-applied-note">
              {travelFloorAppliedNote(result.tolerance_body_heights)}
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-1.5">
          <MetricCell label="Confidence" value={fmtPct(result.confidence * 100)} highlight />

          {result.valid_frames !== undefined && result.total_frames !== undefined && (
            <MetricCell
              label="Valid Frames"
              value={`${result.valid_frames}/${result.total_frames}`}
            />
          )}

          {isMovement && result.trajectory_result ? (
            <>
              {result.trajectory_result.direction_compliance !== undefined && (
                <MetricCell
                  label="Direction"
                  value={fmtPct(result.trajectory_result.direction_compliance * 100)}
                />
              )}
            </>
          ) : (
            <>
              {result.delta_percent !== undefined && (
                <MetricCell label="Delta" value={fmtPct(result.delta_percent)} />
              )}
            </>
          )}
        </div>
      )}

      {/* Movement-specific extras (non-travel modes only — travel has its
          own block above, and its `trajectory_result` snapshot predates
          scoring.py's mode/displacement fields in a different shape). */}
      {isMovement && !isTravel && result.trajectory_result && (
        <div className="grid grid-cols-3 gap-1.5">
          {result.trajectory_result.displacement_ratio !== undefined && (
            <MetricCell
              label="Displacement"
              value={fmtRatio(result.trajectory_result.displacement_ratio)}
            />
          )}
          <MetricCell
            label="Mode"
            value={result.trajectory_result.mode.replace(/_/g, ' ')}
          />
        </div>
      )}

      {/* Coach's note */}
      <CoachNoteBlock note={result.annotation_notes} />

      {/* `explanation` is backend internal/log-facing copy (contract §4's
          own note) and for travel embeds a raw landmark identifier
          (`effective_source_landmark`) — the clean sentence above already
          says everything a coach needs, so the raw string is never
          rendered for a travel result. */}
      {!isTravel && result.explanation && (
        <p className="text-xs text-text-secondary leading-relaxed pt-1 border-t border-border-subtle/30">
          {plainGoalExplanation(result.explanation)}
        </p>
      )}
    </div>
  );
}

export default GoalResultsSection;
